import { Button, Card, DatePicker, Form, Grid, Input, Message, Select, Space, Tabs } from "@arco-design/web-react"
import { CustomerAPI, SOAPI, InvoiceAPI, SOFulfillmentAPI } from "@/apis/sales";
import { ItemAPI } from "@/apis/material";
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Currency } from "@/components/Numeric";
import { InputNumber, InputCurrency } from "@/components/Numeric";
import { EnumSelect } from "@/components/Enum";
import { TermsDef } from "@/enums/common";
import { ReferenceTypeDef } from "@/enums/purchasing";
import CustomerSelect from "@/components/CustomerSelect";
import AccountSelect from "@/components/AccountSelect";
import { IconDelete, IconPlus } from "@arco-design/web-react/icon";
import { formatDate, TermUtil, today } from "@/utils";
import _ from "lodash";
import Decimal from "decimal.js";

const Row = Grid.Row;
const Col = Grid.Col;
const FormItem = Form.Item;
const TextArea = Input.TextArea;
const TabPane = Tabs.TabPane;

export const InvoiceAdd = () => {
    const navigate = useNavigate();
    const params = useParams();
    const [searchParams] = useSearchParams();
    const soId = params.soId;
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);
    const [soList, setSOList] = useState();
    const [items, setItems] = useState([]);
    const [totalItems, setTotalItems] = useState(0);
    const [totalRevenue, setTotalRevenue] = useState(0);
    const [fulfillmentIds, setFulfillmentIds] = useState([]);

    const calcDueDate = useCallback(() => {
        const date = form.getFieldValue("invoiceDate");
        const term = form.getFieldValue("term");
        form.setFieldValue("dueDate", formatDate(TermUtil.dueDate(date, term)));
    }, [form]);

    const getItems = useCallback(async (invoiceItems) => {
        const nonInventoryItems = await ItemAPI.getNonInventoryItems();
        let fulfilledItems = [];
        if (invoiceItems) {
            fulfilledItems = _.map(invoiceItems, (item) => {
                return {
                    id: item.itemId,
                    itemName: item.itemName,
                    uom: item.uom
                }
            })
        }
        let items = _.uniqBy(_.concat(fulfilledItems, nonInventoryItems), 'id');
        items = _.sortBy(items, "itemName");
        setItems(items);
    }, [])

    const onCustomerSelect = useCallback(async (val) => {
        const customer = await CustomerAPI.get(val);
        form.setFieldValue("address", `${customer.billingAddress}, ${customer.billingCity}, ${customer.billingState} ${customer.billingZip}`)
        form.setFieldValue("term", customer.term);
        calcDueDate();
    }, [form, calcDueDate])

    const onItemSelect = async (field, val) => {
        const customerId = form.getFieldValue("customerId");
        const soItem = await InvoiceAPI.getCustomerLastInvoiceItem(customerId, val);
        const item = await ItemAPI.get(val);
        form.setFieldValue(`${field}.description`, item.description);
        form.setFieldValue(`${field}.uom`, item.uom);
        form.setFieldValue(`${field}.cost`, soItem ? soItem.cost : item.salePrice);
    }

    const onItemChange = (field) => {
        const quantity = new Decimal(form.getFieldValue(`${field}.quantity`));
        const cost = new Decimal(form.getFieldValue(`${field}.cost`));
        const amount = cost.mul(quantity);
        form.setFieldValue(`${field}.amount`, amount);
        calcTotal();
    }

    const onRevenueAmountChange = (val) => {
        calcTotal();
    }

    const calcTotal = useCallback(() => {
        const items = form.getFieldValue("items");
        const revenues = form.getFieldValue("revenues");
        let totalItems = new Decimal(0);
        let totalRevenues = new Decimal(0);
        for (const item of items ?? []) {
            totalItems = totalItems.plus(item.amount);
        }
        for (const revenue of revenues ?? []) {
            totalRevenues = totalRevenues.plus(revenue.amount);
        }
        setTotalItems(totalItems.toNumber());
        setTotalRevenue(totalRevenues.toNumber());
        form.setFieldValue("totalAmount", totalItems.plus(totalRevenues));
    }, [form]);

    const getCurrentSOs = useCallback(async (soNo) => {
        const values = await SOAPI.query(1, {
            soNo: soNo,
            status: [1, 2, 3, 4, 5, 6, 7, 8, 9, 11, 12]
        });
        setSOList(values.records);
    }, [])

    useEffect(() => {
        getCurrentSOs();
    }, [getCurrentSOs])

    useEffect(() => {
        const init = async () => {
            const so = await SOAPI.get(soId);
            const customer = await CustomerAPI.get(so.customerId);
            const invoice = {
                customerId: so.customerId,
                address: `${customer.billingAddress}, ${customer.billingCity}, ${customer.billingState} ${customer.billingZip}`,
                term: customer.term,
                soId: so.id,
                soNo: so.soNo,
                invoiceDate: today(),
                reference: so.reference,
                revenues: [],
            }

            const fIds = searchParams.getAll("fulfillmentId");
            setFulfillmentIds(fIds);

            let inventoryItems = [];
            if (fIds && fIds.length > 0) {
                for (const index in fIds) {
                    const fulfillment = await SOFulfillmentAPI.get(fIds[index]);
                    const fItems = _.map(fulfillment.items, (o) => ({
                        itemId: o.itemId,
                        itemName: o.itemName,
                        description: o.description,
                        quantity: o.qtyFulfilled,
                        uom: o.uom,
                        cost: _.find(so.items, (soItem) => soItem.itemId === o.itemId).rate
                    }));
                    inventoryItems = _.concat(inventoryItems, fItems);
                    invoice.invoiceDate = fulfillment.fulfillmentDate;
                }

                _.forEach(inventoryItems, (o) => {
                    o.amount = Decimal.mul(o.quantity, o.cost).toNumber();
                })
            } else {
                inventoryItems = _.map(_.filter(so.items, (item) => item.itemType === 1), (v) => ({
                    itemId: v.itemId,
                    itemName: v.itemName,
                    description: v.description,
                    quantity: v.quantity,
                    uom: v.uom,
                    cost: v.rate,
                    amount: v.amount
                }));
            }

            let invoicedItems = await InvoiceAPI.getItemsBySoId(soId);
            invoicedItems = _.reduce(_.groupBy(invoicedItems, 'itemId'), (result, val, key) => {
                const total = new Decimal(0);
                for (const item of val) {
                    total.plus(new Decimal(item.quantity));
                }
                result[key] = total.toNumber();
                return result;
            }, {})

            let nonInventoryItems = await SOAPI.getNonInventoryItems(soId);
            nonInventoryItems = _.map(nonInventoryItems, (item) => ({
                itemId: item.itemId,
                itemName: item.itemName,
                description: item.description,
                quantity: item.quantity,
                uom: item.uom,
                cost: item.rate,
                amount: item.amount
            }));
            _.forEach(nonInventoryItems, (item) => {
                item.quantity = Decimal.sub(item.quantity, invoicedItems[item.itemId] ?? 0).toNumber();
                item.amount = Decimal.mul(item.quantity, item.cost).toNumber();
            });
            nonInventoryItems = _.filter(nonInventoryItems, (item) => item.quantity > 0);

            let allItems = _.concat(inventoryItems, nonInventoryItems);
            invoice.items = _.sortBy(allItems, "itemName");

            await getItems(invoice.items);
            form.setFieldsValue(invoice);
            if (!invoice.dueDate) {
                calcDueDate();
            }
            calcTotal();
        }

        init();
    }, [soId, searchParams, form, getItems, calcDueDate, calcTotal]);

    const save = async () => {
        try {
            const values = await form.validate();
            setSaving(true);
            values.fulfillmentIds = fulfillmentIds;
            const invoiceId = await InvoiceAPI.add(values);
            Message.success("Invoice created successfully");
            navigate(`/workspace/sales/invoice/${invoiceId}`);
        } catch (error) {
            if (error.name !== 'ValidationError') {
                if (error.status === "909001") {
                    Message.warning(error.message);
                } else {
                    Message.error(error.message || "Failed to create Invoice");
                }
            }
        } finally {
            setSaving(false);
        }
    }

    return (
        <Card title="Invoice" extra={
            <Space>
                <Button type="primary" onClick={() => save()} loading={saving}>Save</Button>
                <Link to={`/workspace/sales/so/${soId}`}>
                    <Button>Cancel</Button>
                </Link>
            </Space>
        }>
            <Form form={form} colon labelCol={{ span: 6 }} wrapperCol={{ span: 18 }}>
                <FormItem field="id" hidden>
                    <Input />
                </FormItem>

                <Row gutter={12}>
                    <Col span={12}>
                        <FormItem field="customerId" label="Customer">
                            <CustomerSelect onChange={(val) => onCustomerSelect(val)} />
                        </FormItem>
                    </Col>
                    <Col span={6}>
                        <FormItem label="Invoice Date" field="invoiceDate"
                            labelCol={{ span: 10 }}
                            wrapperCol={{ span: 14 }}
                            required rules={[{ required: true }]}>
                            <DatePicker onChange={() => calcDueDate()} />
                        </FormItem>
                    </Col>
                    <Col span={6}>
                        <FormItem field="soId" label="SO#"
                            labelCol={{ span: 10 }}
                            wrapperCol={{ span: 14 }}
                        >
                            <Select options={_.map(soList, (item) => ({ value: item.id, label: item.soNo }))} />
                        </FormItem>
                    </Col>
                </Row>
                <Row gutter={12}>
                    <Col span={12}>
                        <FormItem label="Address" field="address">
                            <Input readOnly />
                        </FormItem>
                    </Col>
                    <Col span={6}>
                        <FormItem label="Terms" field="term"
                            labelCol={{ span: 10 }} wrapperCol={{ span: 14 }}
                            required rules={[{ required: true }]}>
                            <EnumSelect enumObj={TermsDef} onChange={(val) => {
                                calcDueDate();
                            }} />
                        </FormItem>
                    </Col>
                    <Col span={6}>
                        <FormItem label="Due Date" field="dueDate"
                            labelCol={{ span: 10 }} wrapperCol={{ span: 14 }}
                            required rules={[{ required: true }]}>
                            <DatePicker />
                        </FormItem>
                    </Col>
                </Row>
                <Row gutter={12}>
                    <Col span={18}>
                        <FormItem label="Reference#" field="reference"
                            labelCol={{ span: 4 }}>
                            <Form.List field="reference">
                                {(fields, { add, remove, move }) =>
                                    <>
                                        {fields.map((item, index) => {
                                            return (
                                                <FormItem key={item.key} style={{ marginBottom: 0 }}>
                                                    <Space className="form-item-row">
                                                        <FormItem field={item.field + ".type"}>
                                                            <EnumSelect enumObj={ReferenceTypeDef} order="label" style={{ width: 200 }} />
                                                        </FormItem>
                                                        <FormItem field={item.field + ".number"}>
                                                            <Input />
                                                        </FormItem>
                                                        <Button type="text" status="danger" icon={<IconDelete />} onClick={() => remove(index)}></Button>
                                                    </Space>
                                                </FormItem>
                                            )
                                        })}
                                        <Button type="text" size="mini" icon={<IconPlus />} onClick={() => add()}>Add Reference</Button>
                                    </>
                                }
                            </Form.List>
                        </FormItem>
                    </Col>
                    <Col span={6}>
                        <FormItem field="totalAmount" label="Total Amount"
                            labelCol={{ span: 10 }} wrapperCol={{ span: 14 }} >
                            <InputCurrency readOnly />
                        </FormItem>
                    </Col>
                </Row>
                <Row>
                    <Col span={24}>
                        <FormItem label="Memo" field="memo"
                            labelCol={{ span: 3 }} wrapperCol={{ span: 21 }}>
                            <TextArea />
                        </FormItem>
                    </Col>
                </Row>
                <Row>
                    <Col span={24}>
                        <Tabs defaultActiveTab="1" type="card-gutter" lazyload={false}>
                            <TabPane key="1" title={
                                <Space size={80}>
                                    <span>Items:</span>
                                    <span><Currency value={totalItems} /></span>
                                </Space>
                            }>

                                <Form.List field="items">
                                    {(fields, { add, remove, move }) =>
                                        <>
                                            <table style={{ width: "100%" }}>
                                                <thead>
                                                    <tr>
                                                        <th width="250">Item</th>
                                                        <th>Description</th>
                                                        <th width="120">Qty</th>
                                                        <th width="70">U/M</th>
                                                        <th width="120">Price</th>
                                                        <th width="120">Amount</th>
                                                        <th width="50"></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {fields.map((item, index) => (
                                                        <tr key={index}>
                                                            <td>
                                                                <FormItem field={`${item.field}.id`} hidden>
                                                                    <Input />
                                                                </FormItem>
                                                                <FormItem field={`${item.field}.itemId`} noStyle>
                                                                    <Select showSearch options={_.map(items, (item) => ({ value: item.id, label: item.itemName }))} onChange={(val) => onItemSelect(item.field, val)} />
                                                                </FormItem>
                                                            </td>
                                                            <td>
                                                                <FormItem field={`${item.field}.description`} noStyle>
                                                                    <Input />
                                                                </FormItem>
                                                            </td>
                                                            <td>
                                                                <FormItem field={`${item.field}.quantity`} noStyle>
                                                                    <InputNumber onChange={(val) => onItemChange(item.field, val)} />
                                                                </FormItem>
                                                            </td>
                                                            <td>
                                                                <FormItem field={`${item.field}.uom`} noStyle>
                                                                    <Input readOnly />
                                                                </FormItem>
                                                            </td>
                                                            <td>
                                                                <FormItem field={`${item.field}.cost`} noStyle>
                                                                    <InputNumber onChange={(val) => onItemChange(item.field, val)} />
                                                                </FormItem>
                                                            </td>
                                                            <td>
                                                                <FormItem field={`${item.field}.amount`} noStyle>
                                                                    <InputNumber readOnly />
                                                                </FormItem>
                                                            </td>
                                                            <td>
                                                                <Button type="text" icon={<IconDelete />}
                                                                    status="danger"
                                                                    onClick={() => remove(index)} />
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                            <Button type="text" size="mini" icon={<IconPlus />} style={{ margin: "10px" }}
                                                onClick={() => add()}>Add</Button>
                                        </>
                                    }
                                </Form.List>
                            </TabPane>
                            <TabPane key="2" title={
                                <Space size={80}>
                                    <span>Revenue:</span>
                                    <span><Currency value={totalRevenue} /></span>
                                </Space>
                            }>
                                <Form.List field="revenues">
                                    {(fields, { add, remove, move }) =>
                                        <>
                                            <table style={{ width: "100%" }}>
                                                <thead>
                                                    <tr>
                                                        <th width="250">Account</th>
                                                        <th width="200">Amount</th>
                                                        <th>Memo</th>
                                                        <th width="50"></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {fields.map((item, index) => (
                                                        <tr key={index}>
                                                            <td>
                                                                <FormItem field={`${item.field}.id`} hidden>
                                                                    <Input />
                                                                </FormItem>
                                                                <FormItem field={`${item.field}.accountId`} rules={[{ required: true }]} noStyle>
                                                                    <AccountSelect type="1" />
                                                                </FormItem>
                                                            </td>
                                                            <td>
                                                                <FormItem field={`${item.field}.amount`} rules={[{ required: true }]} noStyle>
                                                                    <InputNumber onChange={(val) => onRevenueAmountChange(val)} />
                                                                </FormItem>
                                                            </td>
                                                            <td>
                                                                <FormItem field={`${item.field}.memo`} noStyle>
                                                                    <Input />
                                                                </FormItem>
                                                            </td>
                                                            <td>
                                                                <Button type="text" icon={<IconDelete />}
                                                                    status="danger"
                                                                    onClick={() => remove(index)} />
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                            <Button type="text" size="mini" icon={<IconPlus />} style={{ margin: "10px" }}
                                                onClick={() => add()}>Add</Button>
                                        </>
                                    }
                                </Form.List>
                            </TabPane>

                        </Tabs>
                    </Col>
                </Row>
            </Form>
        </Card>
    )
}
